// صفحهٔ «بازیابی» — کاملاً جدا از بکاپ و بروزرسانی.
// سه بخشِ مستقل: بازیابی دیتابیس / بازیابی فایل‌ها و ضمیمه‌ها / بازیابی کاملِ سیستم.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Checkbox, Col, Descriptions, Radio, Result, Row, Space, Steps, Tabs, Tag, Timeline, Typography, Upload, message } from "antd";
import Alert from "../../components/GxAlert";
import {
  DatabaseOutlined, FileZipOutlined, ReloadOutlined, SafetyCertificateOutlined,
  ThunderboltOutlined, UploadOutlined,
} from "@ant-design/icons";
import {
  getBackupConfig, listBackups, restoreDatabase, restoreFiles, restoreFull,
  restorePreflight, uploadBackup,
} from "../../api/client";
import DismissibleHelp from "../../components/DismissibleHelp";
import BackupHistoryTable, { humanSize } from "./backup/BackupHistoryTable";
import DangerConfirmModal from "./backup/DangerConfirmModal";

const { Text, Title } = Typography;

const FILE_MODES = [
  { value: "merge", label: "ادغام با فایل‌های موجود", hint: "فقط فایل‌هایی که محتوایشان فرق دارد نوشته می‌شوند." },
  { value: "replace", label: "جایگزینی کامل", hint: "همهٔ فایل‌های آرشیو بازنویسی می‌شوند." },
  { value: "missing_only", label: "فقط فایل‌های حذف‌شده", hint: "فقط فایل‌هایی که الان وجود ندارند برگردانده می‌شوند." },
];

/** نتیجهٔ عملیات به‌صورتِ خطِ زمانیِ مراحل. */
function StepsResult({ result }) {
  if (!result) return null;
  const steps = result.steps || result.log || [];
  return (
    <Card size="small" style={{ marginTop: 12 }}
      title={<Space>نتیجه
        <Tag color={result.ok ? "green" : result.rolled_back ? "orange" : "red"}>
          {result.ok ? "موفق" : result.rolled_back ? "برگشت به وضعیت قبل" : "ناموفق"}
        </Tag></Space>}>
      {result.message && (
        <Alert type={result.ok ? "success" : "warning"} showIcon message={result.message}
          style={{ marginBottom: 12 }} />
      )}
      {steps.length > 0 && (
        <Timeline items={steps.map((s) => ({
          color: s.status === "ok" ? "green" : s.status === "info" ? "blue" : "red",
          children: (
            <div>
              <Text strong>{s.step}</Text>
              {s.detail && <div style={{ fontSize: 12, color: "#888", whiteSpace: "pre-wrap" }}>{s.detail}</div>}
            </div>
          ),
        }))} />
      )}
      {typeof result.restored === "number" && (
        <Descriptions size="small" column={2} bordered style={{ marginTop: 8 }}>
          <Descriptions.Item label="بازگردانی‌شده">{result.restored}</Descriptions.Item>
          <Descriptions.Item label="یکسان (بدون تغییر)">{result.identical}</Descriptions.Item>
          <Descriptions.Item label="ردشده">{result.skipped}</Descriptions.Item>
          <Descriptions.Item label="مسدودشده">{(result.blocked || []).length}</Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  );
}

/** انتخاب‌گرِ بکاپ: از تاریخچه یا با آپلودِ فایل. */
function BackupPicker({ kind, selectedId, onSelect, reloadKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    listBackups(kind).then((r) => setRows(r.data.filter((x) => x.status === "ok")))
      .catch(() => message.error("خطا در بارگذاریِ لیست"))
      .finally(() => setLoading(false));
  }, [kind]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const onUpload = async (file) => {
    try {
      const { data } = await uploadBackup(file, kind);
      if (data.validation?.ok) { message.success("فایل آپلود و تأیید شد"); onSelect(data.id); }
      else message.error("فایلِ آپلودشده معتبر نیست: " + (data.validation?.message || ""));
      load();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در آپلود"); }
    return false;
  };

  return (
    <Card size="small" title="۱ — انتخابِ نسخهٔ بکاپ"
      extra={<Space>
        <Upload beforeUpload={onUpload} showUploadList={false}
          accept={kind === "database" ? ".dump,.sql,.backup,.enc" : ".tar.gz,.tgz,.gz,.enc"}>
          <Button size="small" icon={<UploadOutlined />}>آپلودِ فایل</Button>
        </Upload>
        <Button size="small" icon={<ReloadOutlined />} onClick={load}>تازه‌سازی</Button>
      </Space>}>
      <BackupHistoryTable rows={rows} loading={loading} compact
        selectedId={selectedId} onSelect={onSelect} pageSize={6} />
    </Card>
  );
}

/** کارتِ اطلاعاتِ بکاپِ انتخاب‌شده + بررسیِ صحت (بدونِ هیچ تغییری). */
function PreflightCard({ backupId, onLoaded }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  // onLoaded در ref نگه داشته می‌شود تا تغییرِ هویتِ تابع باعثِ fetch مجدد نشود
  const loadedRef = useRef(onLoaded);
  loadedRef.current = onLoaded;

  useEffect(() => {
    if (!backupId) { setInfo(null); loadedRef.current?.(null); return; }
    setLoading(true);
    restorePreflight(backupId)
      .then((r) => { setInfo(r.data); loadedRef.current?.(r.data); })
      .catch(() => message.error("خطا در بررسیِ بکاپ"))
      .finally(() => setLoading(false));
  }, [backupId]);

  if (!backupId) return null;
  return (
    <Card size="small" loading={loading} style={{ marginTop: 12 }}
      title={<Space><SafetyCertificateOutlined />۲ — بررسیِ صحت و اطلاعاتِ بکاپ</Space>}>
      {info && (
        <>
          <Alert type={info.valid ? "success" : "error"} showIcon style={{ marginBottom: 12 }}
            message={info.valid ? "فایلِ بکاپ سالم است و قابلِ بازیابی." : "فایلِ بکاپ معتبر نیست."}
            description={info.validation?.message || null} />
          <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered>
            <Descriptions.Item label="نامِ فایل">{info.backup.filename}</Descriptions.Item>
            <Descriptions.Item label="تاریخ">{(info.backup.created_at || "").slice(0, 16)}</Descriptions.Item>
            <Descriptions.Item label="نسخهٔ برنامه">{info.backup.app_version || "—"}</Descriptions.Item>
            <Descriptions.Item label="حجم">{humanSize(info.backup.size_bytes)}</Descriptions.Item>
            <Descriptions.Item label="رمزنگاری">{info.backup.encrypted ? "بله" : "خیر"}</Descriptions.Item>
            <Descriptions.Item label="چک‌سام">
              <Text code style={{ fontSize: 11 }}>{(info.backup.checksum || "—").slice(0, 24)}</Text>
            </Descriptions.Item>
          </Descriptions>
          {info.preview && (
            <div style={{ marginTop: 10 }}>
              <Text strong>محتوای آرشیو: </Text>
              <Space wrap>
                {Object.entries(info.preview.by_root || {}).map(([k, v]) => (
                  <Tag key={k} color="blue">{k}: {v.files} فایل</Tag>
                ))}
              </Space>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ─────────────────────────── بازیابیِ دیتابیس ───────────────────────────
function RestoreDatabasePanel({ modules }) {
  const [selId, setSelId] = useState(null);
  const [info, setInfo] = useState(null);
  const [mode, setMode] = useState("full");
  const [mods, setMods] = useState([]);
  const [runMig, setRunMig] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async ({ admin_pin, confirm_text }) => {
    setBusy(true);
    try {
      const { data } = await restoreDatabase({
        backup_id: selId, mode, modules: mods, admin_pin, confirm_text,
        run_migrations: runMig, safety: true,
      });
      setResult(data);
      setConfirmOpen(false);
      if (data.ok) message.success("بازیابیِ دیتابیس انجام شد");
      else message.warning(data.message || "بازیابی ناموفق بود");
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در بازیابی"); }
    setBusy(false);
  };

  const ready = selId && info?.valid && (mode === "full" || mods.length > 0);

  return (
    <div style={{ direction: "rtl" }}>
      <DismissibleHelp id="restore-db" type="warning"
        message="بازیابیِ دیتابیس دادهٔ فعلی را با دادهٔ بکاپ جایگزین می‌کند."
        description="قبل از هر بازیابی، یک بکاپِ ایمنی از وضعیتِ فعلی گرفته می‌شود و اگر هر مرحله شکست بخورد، همان خودکار برگردانده می‌شود." />
      <BackupPicker kind="database" selectedId={selId} onSelect={setSelId} />
      <PreflightCard backupId={selId} onLoaded={setInfo} />

      {selId && (
        <Card size="small" title="۳ — دامنهٔ بازیابی" style={{ marginTop: 12 }}>
          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
            <Radio value="full">کاملِ دیتابیس</Radio>
            <Radio value="selective">انتخابی (به‌تفکیکِ ماژول)</Radio>
          </Radio.Group>
          {mode === "selective" && (
            <div style={{ marginTop: 10 }}>
              <Checkbox.Group value={mods} onChange={setMods}
                options={Object.entries(modules || {}).map(([v, l]) => ({ label: l, value: v }))} />
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <Checkbox checked={runMig} onChange={(e) => setRunMig(e.target.checked)}>
              اجرای مهاجرت‌های سازگار بعد از بازیابی (توصیه می‌شود)
            </Checkbox>
          </div>
          <Button danger type="primary" block style={{ marginTop: 12 }} disabled={!ready}
            onClick={() => setConfirmOpen(true)}>
            بازیابیِ دیتابیس
          </Button>
        </Card>
      )}

      <StepsResult result={result} />

      <DangerConfirmModal
        open={confirmOpen} confirmLoading={busy} word="بازیابی"
        title="تأییدِ بازیابیِ دیتابیس"
        warning="دادهٔ فعلیِ جدول‌های انتخاب‌شده با دادهٔ بکاپ جایگزین می‌شود."
        checklist={[
          "یک بکاپِ ایمنی از وضعیتِ فعلی گرفته می‌شود",
          mode === "full" ? "همهٔ جدول‌های برنامه بازیابی می‌شوند" : `فقط ماژول‌های: ${mods.join("، ")}`,
          runMig ? "مهاجرت‌های سازگار اجرا می‌شوند" : "مهاجرت اجرا نمی‌شود",
          "بعد از بازیابی، سلامتِ سیستم بررسی می‌شود",
          "در صورتِ شکست، وضعیتِ قبلی خودکار برمی‌گردد",
        ]}
        onCancel={() => setConfirmOpen(false)} onConfirm={run} />
    </div>
  );
}

// ─────────────────────────── بازیابیِ فایل‌ها ───────────────────────────
function RestoreFilesPanel() {
  const [selId, setSelId] = useState(null);
  const [info, setInfo] = useState(null);
  const [mode, setMode] = useState("merge");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async ({ admin_pin, confirm_text }) => {
    setBusy(true);
    try {
      const { data } = await restoreFiles({
        backup_id: selId, mode, admin_pin, confirm_text, snapshot: true,
      });
      setResult(data);
      setConfirmOpen(false);
      if (data.ok) message.success(`${data.restored} فایل بازگردانی شد`);
      else message.warning(data.message || "بازیابی ناموفق بود");
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در بازیابی"); }
    setBusy(false);
  };

  return (
    <div style={{ direction: "rtl" }}>
      <DismissibleHelp id="restore-files"
        message="بازیابیِ فایل‌ها هیچ‌وقت فایلی را که در آرشیو نیست حذف نمی‌کند."
        description="فایل‌های فعلیِ شما امن‌اند؛ فقط فایل‌های داخلِ آرشیو بسته به حالتِ انتخابی نوشته می‌شوند. قبل از شروع هم یک عکسِ ایمنی از وضعیتِ فعلی گرفته می‌شود." />
      <BackupPicker kind="files" selectedId={selId} onSelect={setSelId} />
      <PreflightCard backupId={selId} onLoaded={setInfo} />

      {selId && (
        <Card size="small" title="۳ — حالتِ بازیابی" style={{ marginTop: 12 }}>
          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
            <Space direction="vertical">
              {FILE_MODES.map((m) => (
                <Radio key={m.value} value={m.value}>
                  {m.label}
                  <div style={{ fontSize: 12, color: "#888" }}>{m.hint}</div>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
          <Button danger type="primary" block style={{ marginTop: 12 }}
            disabled={!info?.valid} onClick={() => setConfirmOpen(true)}>
            بازیابیِ فایل‌ها و ضمیمه‌ها
          </Button>
        </Card>
      )}

      <StepsResult result={result} />
      {result?.blocked?.length > 0 && (
        <Card size="small" title="فایل‌های مسدودشده" style={{ marginTop: 12 }}>
          {result.blocked.slice(0, 20).map((b) => (
            <div key={b.name} style={{ fontSize: 12 }}>
              <Text code>{b.name}</Text> — <Text type="secondary">{b.reason}</Text>
            </div>
          ))}
        </Card>
      )}

      <DangerConfirmModal
        open={confirmOpen} confirmLoading={busy} word="بازیابی"
        title="تأییدِ بازیابیِ فایل‌ها و ضمیمه‌ها"
        warning="فایل‌های برنامه با نسخهٔ داخلِ آرشیو بازنویسی می‌شوند."
        checklist={[
          "یک عکسِ ایمنی از فایل‌های فعلی گرفته می‌شود",
          `حالت: ${FILE_MODES.find((m) => m.value === mode)?.label}`,
          "هیچ فایلی که در آرشیو نباشد حذف نمی‌شود",
          "مسیرهای حساس (‎.env، ‎.git، backups) دست‌نخورده می‌مانند",
        ]}
        onCancel={() => setConfirmOpen(false)} onConfirm={run} />
    </div>
  );
}

// ─────────────────────────── بازیابیِ کاملِ سیستم ───────────────────────────
function RestoreFullPanel() {
  const [dbId, setDbId] = useState(null);
  const [filesId, setFilesId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async ({ admin_pin, confirm_text }) => {
    setBusy(true);
    try {
      const { data } = await restoreFull({
        db_backup_id: dbId, files_backup_id: filesId, admin_pin, confirm_text,
        run_migrations: true,
      });
      setResult(data);
      setConfirmOpen(false);
      if (data.ok) message.success("بازیابیِ کاملِ سیستم انجام شد");
      else message.warning(data.message || "بازیابی ناموفق بود");
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در بازیابی"); }
    setBusy(false);
  };

  return (
    <div style={{ direction: "rtl" }}>
      <DismissibleHelp id="restore-full" type="warning"
        message="بازیابیِ کاملِ سیستم هر دو بخش را به ترتیبِ امن اجرا می‌کند: اول دیتابیس، بعد فایل‌ها."
        description="اگر بازیابیِ دیتابیس شکست بخورد، بازیابیِ فایل‌ها اصلاً شروع نمی‌شود." />
      <Steps size="small" current={dbId && filesId ? 2 : dbId ? 1 : 0}
        style={{ marginBottom: 16 }}
        items={[{ title: "بکاپ دیتابیس" }, { title: "بکاپ فایل‌ها" }, { title: "تأیید و اجرا" }]} />
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}><BackupPicker kind="database" selectedId={dbId} onSelect={setDbId} /></Col>
        <Col xs={24} lg={12}><BackupPicker kind="files" selectedId={filesId} onSelect={setFilesId} /></Col>
      </Row>
      <Button danger type="primary" block style={{ marginTop: 12 }}
        disabled={!dbId || !filesId} onClick={() => setConfirmOpen(true)}>
        بازیابیِ کاملِ سیستم
      </Button>

      {result && (
        <>
          <Result style={{ paddingBottom: 0 }}
            status={result.ok ? "success" : "warning"}
            title={result.message}
            subTitle={`مرحلهٔ متوقف‌شده: ${result.stage || "—"}`} />
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Title level={5}>دیتابیس</Title>
              <StepsResult result={result.database} />
            </Col>
            <Col xs={24} lg={12}>
              <Title level={5}>فایل‌ها</Title>
              <StepsResult result={result.files} />
            </Col>
          </Row>
        </>
      )}

      <DangerConfirmModal
        open={confirmOpen} confirmLoading={busy} word="بازیابی"
        title="تأییدِ بازیابیِ کاملِ سیستم"
        warning="هم دیتابیس و هم فایل‌های برنامه بازیابی می‌شوند."
        checklist={[
          "بکاپِ ایمنی از دیتابیس و فایل‌های فعلی گرفته می‌شود",
          "اول دیتابیس بازیابی می‌شود، بعد فایل‌ها",
          "اگر دیتابیس شکست بخورد، فایل‌ها دست‌نخورده می‌مانند",
          "بعد از هر مرحله سلامتِ سیستم بررسی می‌شود",
        ]}
        onCancel={() => setConfirmOpen(false)} onConfirm={run} />
    </div>
  );
}

export default function RestoreTab() {
  const [modules, setModules] = useState({});
  useEffect(() => {
    getBackupConfig().then((r) => setModules(r.data.modules || {})).catch(() => {});
  }, []);

  const items = [
    {
      key: "database", label: <Space><DatabaseOutlined />بازیابی دیتابیس</Space>,
      children: <RestoreDatabasePanel modules={modules} />,
    },
    {
      key: "files", label: <Space><FileZipOutlined />بازیابی فایل‌ها و ضمیمه‌ها</Space>,
      children: <RestoreFilesPanel />,
    },
    {
      key: "full", label: <Space><ThunderboltOutlined />بازیابی کاملِ سیستم</Space>,
      children: <RestoreFullPanel />,
    },
  ];
  return <div style={{ direction: "rtl" }}><Tabs items={items} /></div>;
}
